import React from 'react';
import { Flex } from '@adobe/react-spectrum';
import './Bot.css';
import { Helmet } from 'react-helmet';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { NitroAd } from '../../ads/NitroAd';
import { DiscordCtaButton } from '../common/discordCta/DiscordCtaButton';
import { DISCORD_BOT_INSTALL_URL } from '../../config/socialLinks';

// Everything the /check bracket card reports, rendered as the feature grid.
const CARD_FEATURES = [
    {
        title: 'Baseline bracket (WotC spec)',
        description:
            'The bracket your list qualifies for by the letter of the official spec — scored on early combos, extra turns, game changers, and mass land denial.',
    },
    {
        title: 'Realistic bracket',
        description:
            'Where the deck actually plays, scored on consistency, efficiency, interaction, manabase, and win conditions.',
    },
    {
        title: 'Axis-by-axis bars',
        description:
            'Every scoring axis gets its own 1–5 bar, so you can see exactly which knobs pushed the deck into its bracket.',
    },
    {
        title: 'Game changers',
        description:
            'The game-changer cards in the list, called out by name — no arguing about whether The One Ring counts.',
    },
    {
        title: 'Deep links',
        description:
            'A QR code plus Deck List and Commandersalt Analysis buttons, so anyone at the table can jump to the full breakdown.',
    },
    {
        title: 'Receipts included',
        description:
            'Each card is stamped with its generation date and API version, so everyone knows the read is current.',
    },
];

export function Bot() {
    const helmetName = 'Discord Bot';
    const helmetCanonical = 'https://www.commandersalt.com/bot';
    const helmetDescription = 'Get bracket checks for any deck, right inside Discord.';
    const helmetTitle = 'Commandersalt.com - Discord Bot';

    return (
        <Flex direction="column">
            <Helmet>
                <title>{helmetName}</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href={helmetCanonical} />
                <meta name="description" content={helmetDescription} />
                <meta name="twitter:title" content={helmetTitle} />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content={helmetTitle} />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content={helmetDescription} />
            </Helmet>
            <BreadCrumbs />

            <div className="botPageRoot">
                <section className="botPageHero panelGradient">
                    <div className="botPageHeroMain">
                        <span className="botPageHeroEyebrow">Discord Bot</span>
                        <h1 className="botPageHeroTitle">Bracket checks without leaving Discord</h1>
                        <p className="botPageHeroSubtitle">
                            Someone drops a deck link in your server and swears it&apos;s &ldquo;a bracket 2.&rdquo;
                            Instead of arguing, run <code>/check</code>. The bot analyzes the list and replies with a
                            bracket card — the baseline bracket by the WotC spec, the realistic bracket it actually
                            plays at, and the game changers that got it there — so the whole channel can see the
                            receipts.
                        </p>
                    </div>
                    <div className="botPageHeroActions">
                        <DiscordCtaButton
                            label="Add to your server"
                            url={DISCORD_BOT_INSTALL_URL}
                            analyticsEvent="install_bot_click"
                            analyticsParams={{ placement: 'hero' }}
                        />
                    </div>
                </section>

                <NitroAd id="bot" />

                <section className="botPageSteps" aria-label="How the bot works">
                    <article className="botPageStep panelGradient--leftAccent panelGradient">
                        <div className="botPageStepHeader">
                            <span className="botPageStepNumber">01</span>
                            <h2 className="botPageStepTitle">
                                Run <code>/check</code> with a deck URL
                            </h2>
                        </div>
                        <p className="botPageStepDescription">
                            Paste a link from Moxfield, Archidekt, or any other supported deck builder.
                        </p>
                        <img
                            className="botPageStepImage"
                            src="/bot-screenshot0.png"
                            alt="The /check slash command in Discord with a Moxfield deck URL as input"
                        />
                    </article>
                    <article className="botPageStep panelGradient--leftAccent panelGradient">
                        <div className="botPageStepHeader">
                            <span className="botPageStepNumber">02</span>
                            <h2 className="botPageStepTitle">Get the card</h2>
                        </div>
                        <p className="botPageStepDescription">
                            The bot replies with the bracket card — baseline and realistic brackets, the scoring bars
                            behind each, and the deck&apos;s game changers.
                        </p>
                        <img
                            className="botPageStepImage botPageStepImage--card"
                            src="/bot-screenshot1.png"
                            alt="The bot's reply: a bracket card showing the baseline WotC-spec bracket, the realistic bracket, per-axis scoring bars, and the deck's game-changer cards"
                        />
                    </article>
                </section>

                <section className="botPageFeatures" aria-label="What's on the card">
                    <h2 className="botPageSectionTitle">What&apos;s on the card</h2>
                    <div className="botPageFeatureGrid">
                        {CARD_FEATURES.map((feature) => (
                            <article key={feature.title} className="botPageFeature">
                                <h3 className="botPageFeatureTitle">{feature.title}</h3>
                                <p className="botPageFeatureDescription">{feature.description}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="botPageOutro panelGradient">
                    <p className="botPageOutroText">
                        Free, and no setup beyond the install — add it once and <code>/check</code> works everywhere in
                        the server.
                    </p>
                    <DiscordCtaButton
                        label="Add to your server"
                        url={DISCORD_BOT_INSTALL_URL}
                        analyticsEvent="install_bot_click"
                        analyticsParams={{ placement: 'outro' }}
                    />
                </section>
            </div>
        </Flex>
    );
}
